/** @jsx jsx */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import {
  Button,
  Chip,
  Hidden,
  SelectOption,
  Typography2,
  breakpoints,
  typography2,
  useMediaQuery,
} from '@coursera/cds-core';
import type { Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import RoundedSelectField from 'bundles/cds-labs/components/Modules/CareerCollectionsModule/RoundedSelectField';
import type {
  CareerRoleType,
  CareerRolesCollectionType,
} from 'bundles/cds-labs/components/Modules/CareerCollectionsModule/types/careerRoles';
import ModuleLayout, {
  MODULE_LAYOUTS,
  type ModuleLayoutConfig,
  getModuleLayoutConfig,
} from 'bundles/cds-labs/components/Modules/ModuleLayout';
import { isInvertedColor } from 'bundles/cds-labs/components/Modules/utils/colors';
import { getIsSideBySideLayout } from 'bundles/cds-labs/components/Modules/utils/constants';
import ProductCardsPlaceholder from 'bundles/cds-labs/components/ShowMoreGridSection/ProductCardsPlaceholder';
import {
  type GridConfig,
  ShowMoreGridSection,
} from 'bundles/cds-labs/components/ShowMoreGridSection/ShowMoreGridSection';

import _t from 'i18n!nls/cds-labs';

export type CTAVariant = 'primary' | 'secondary' | 'ghost';

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: CTAVariant | null;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

type DropdownMenu = {
  label?: string;
  options?: {
    value: string;
    label: string;
  }[];
};

export type Filter = {
  id: string;
  filterName: string;
};

type UpsellCard = {
  id: string;
  position: number;
  renderComponent: (props: { index: number }) => React.ReactElement;
};

export type Props = {
  headline?: string | null;
  description?: string | null;
  showAllEntries?: boolean | null;
  filters?: Filter[] | null;
  collection?: CareerRolesCollectionType | null;
  onFilterChange?: (index: number) => void;
  isCollectionLoading?: boolean;
  currentFilterIndex?: number;
  numOfEntriesPerCollection?: number;
  defaultRows?: number;
  defaultMobileRows?: number;
  cta?: CTA;
  dropdownMenu?: DropdownMenu;
  currentDropdownValue?: PathwayLevel;
  onDropdownMenuChange?: (value: PathwayLevel) => void;
  ProductCardComponent: React.ElementType;
  numOfEntriesPerRow?: number;
  onExpandButtonClick?: <T>(value: T) => void;
  showPathwaysLoadingState?: boolean;
  upsellCards?: UpsellCard[];
  overrideItemsGridConfig?: GridConfig;
  overrideContentOffsets?: SerializedStyles;
  additionalTrackingData?: {
    pageConfigComponent?: PageConfigComponentTrackingData;
  };
} & ModuleLayoutConfig;

const styles = {
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  dropdownMenu: css`
    padding: 0;
    margin-left: var(--cds-spacing-200);

    ${breakpoints.up('md')} {
      margin-left: 0;
    }
  `,
  filters: css`
    display: flex;

    && {
      gap: var(--cds-spacing-100);
    }
  `,
  selectOption: css`
    font: ${typography2.actionSecondary.font} !important;
    letter-spacing: ${typography2.actionSecondary.letterSpacing} !important;
    margin: var(--cds-spacing-100);

    .cds-selectOption-container {
      ${typography2.actionSecondary};
      :hover,
      :active {
        color: var(--cds-color-interactive-primary-hover);
        background-color: var(--cds-color-interactive-background-primary-hover-weak);
      }
    }
  `,
  verticalLine: css`
    display: flex;
    border-right: 1px solid var(--cds-color-neutral-disabled-strong);
    height: 32px;
    margin-left: var(--cds-spacing-200);
    margin-right: var(--cds-spacing-200);
  `,
  controls: css`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    align-items: center;
    margin-left: calc(var(--cds-spacing-200) * -1);
    margin-right: calc(var(--cds-spacing-200) * -1);

    ${breakpoints.up('md')} {
      margin-left: 0;
    }

    > * {
      flex-shrink: 0;
    }

    &::-webkit-scrollbar {
      height: 0;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
    }
  `,
  content: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: start;
  `,
  contentOffsets: (isTwoByTenLayout: boolean) => css`
    ${breakpoints.up(isTwoByTenLayout ? 'lg' : 'md')} {
      margin-top: calc(var(--cds-spacing-1400) + var(--cds-spacing-600));
    }
  `,
  contentWithInlineButton: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  `,
  ghostAlignment: css`
    margin-left: calc(var(--cds-spacing-200) * -1);
  `,
  collectionsWrapper: css`
    width: 100%;
  `,
  collection: css`
    .career-card-credentials > div {
      padding: 2px var(--cds-spacing-50);
    }

    && {
      .cds-ProductCard-grid {
        min-width: unset;
      }

      .cds-ProductCard-body > div {
        gap: var(--cds-spacing-100);
      }
    }

    ${breakpoints.up('xs')} {
      .rc-ProductCardsPlaceholder {
        padding-top: 0;

        .cds-ProductCard-grid {
          min-height: 500px;
        }
      }
    }
  `,
  showMoreGridSection: css`
    && {
      padding-top: var(--cds-spacing-150);
      padding-bottom: var(--cds-spacing-200);
    }
  `,
  button: (noTopMargin: boolean) => css`
    && {
      margin: 0;
      margin-top: ${noTopMargin ? 0 : 'var(--cds-spacing-150)'};
      ${breakpoints.down('xs')} {
        max-width: 100%;
      }
    }

    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,
  noResultsFound: css`
    height: 250px;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.up('md')} {
      height: 500px;
    }

    p {
      margin-top: var(--cds-spacing-100);
    }
  `,
  placeholders: css`
    margin-bottom: calc(var(--cds-spacing-600) * -1);
  `,
  upsellContainer: css`
    max-width: 450px;
    height: 100%;

    ${breakpoints.up('sm')} {
      min-width: 230px;
      min-height: 400px;
    }

    ${breakpoints.down('xs')} {
      max-width: none;
    }
  `,
  customImagePanelStyles: (hasFilters: boolean) => css`
    && {
      padding-top: ${hasFilters ? 'var(--cds-spacing-150)' : '0'};
    }
  `,
  customContentPanelStyles: css`
    && {
      padding-bottom: 0;
    }
  `,
  customPageGridContainerStyles: {
    customPaddingTop: css`
      ${breakpoints.down('sm')} {
        && {
          padding-top: var(--cds-spacing-100);
        }
      }
    `,

    default: css`
      && {
        padding-left: var(--cds-spacing-200);
      }
    `,
    threeByNineLayout: css`
      ${breakpoints.up('md')} {
        && {
          padding-left: var(--cds-spacing-400);
        }
      }
    `,
  },
};

function getItemGridConfig(isHorizontal: boolean): GridConfig {
  return isHorizontal ? { lg: 4, md: 4, sm: 6, xs: 12 } : { lg: 3, md: 3, sm: 6, xs: 12 };
}

/**
 * Inserts the first upsell into the collection of roles.
 * Can be updated in the future to handle multiple upsells if needed.
 * but currently that is not something we need to worry about handling.
 */
const insertFirstUpsellIntoCollection = (
  roles: CareerRoleType[],
  upsells: UpsellCard[]
): Array<CareerRoleType | UpsellCard> => {
  if (!upsells.length) return roles;
  const final: Array<CareerRoleType | UpsellCard> = [...roles];
  const firstUpsell = upsells[0];
  // splice automatically handles inserting in the final position if the starting array is too small
  // null check is handled above
  final.splice(firstUpsell!.position, 0, firstUpsell!);
  return final;
};

/**
 * @description The CareerCollectionsModule is a module that displays a collection of career roles with chip filters and pathway level dropdown.
 */
function CareerCollectionsModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    backgroundColor,
    showRoundedCorners,
    cta,
    description,
    headline,
    collection,
    isCollectionLoading,
    onFilterChange,
    currentFilterIndex = 0,
    numOfEntriesPerCollection,
    defaultRows = 1,
    defaultMobileRows = 3,
    dropdownMenu,
    currentDropdownValue,
    onDropdownMenuChange,
    ProductCardComponent,
    onExpandButtonClick,
    showPathwaysLoadingState,
    upsellCards,
    overrideItemsGridConfig,
    overrideContentOffsets,
    additionalTrackingData,
  } = props;
  const track = useTracker();
  const layoutConfig = getModuleLayoutConfig(props);
  const isXsOrLess = useMediaQuery(breakpoints.down('xs'));
  const isSmOrLess = useMediaQuery(breakpoints.down('sm'));
  const isMdOrLess = useMediaQuery(breakpoints.down('md'));
  const isTwoByTenLayout = layout === MODULE_LAYOUTS.text2image10 || layout === MODULE_LAYOUTS.image10text2;
  const isThreeByNineLayout = layout === MODULE_LAYOUTS.text3image9 || layout === MODULE_LAYOUTS.image9text3;
  const shouldRenderContentPanel = headline || description;

  const isHorizontal = getIsSideBySideLayout(layout);
  const itemsGridConfig: GridConfig = overrideItemsGridConfig || getItemGridConfig(isHorizontal);

  // show one less role on tablet or higher, if there is an odd number of roles, to prevent an orphaned card on the last row
  const numOfRolesToDisplay =
    showAllEntries && numOfEntriesPerCollection && numOfEntriesPerCollection % 2 !== 0 && isSmOrLess && !isXsOrLess
      ? numOfEntriesPerCollection - 1
      : numOfEntriesPerCollection;

  const isInverted = isInvertedColor(backgroundColor ?? '');
  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  const rolesToDisplay = collection?.entities?.slice(0, numOfRolesToDisplay) ?? [];

  let rowsToDisplay = defaultRows;
  if (isSmOrLess) rowsToDisplay = Math.floor(defaultMobileRows / 2);
  if (isXsOrLess) rowsToDisplay = defaultMobileRows;

  const filteredRoles =
    currentDropdownValue !== undefined
      ? rolesToDisplay?.map((role) => ({
          ...role,
          ...(role?.pathways && {
            pathways: {
              ...role.pathways,
              entities: role.pathways?.entities?.filter((pathway) => {
                return pathway?.proficiencyLevel && pathway.proficiencyLevel === currentDropdownValue;
              }),
            },
          }),
        }))
      : rolesToDisplay;

  const filteredRolesPlusUpsells: Array<CareerRoleType | UpsellCard> = insertFirstUpsellIntoCollection(
    filteredRoles,
    upsellCards ?? []
  );

  const button = (noTopMargin = false) => {
    return cta?.text && cta?.target ? (
      <div css={cta.variant === 'ghost' ? styles.ghostAlignment : undefined}>
        <Button
          component="a"
          variant={variantOfCta}
          icon={cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
          href={cta.target}
          onClick={cta.onClick}
          css={styles.button(noTopMargin)}
          data-testid="career-collections-module-cta"
        >
          {cta.text}
        </Button>
      </div>
    ) : null;
  };

  const hasFilters = !!filters && filters.length > 1;

  const getCustomPanelStyles = (panelType: 'content' | 'image') => {
    if (!isHorizontal || (isTwoByTenLayout && isMdOrLess) || (isThreeByNineLayout && isSmOrLess)) {
      if (panelType === 'image') {
        return [styles.customImagePanelStyles(!!dropdownMenu || hasFilters)];
      } else {
        return [styles.customContentPanelStyles];
      }
    }

    return [];
  };

  const getCustomPageGridContainerStyles = () => {
    // edge case spacing for career-academy page
    if (!showRoundedCorners && !shouldRenderContentPanel && !isXsOrLess && backgroundColor === 'white') {
      return [styles.customPageGridContainerStyles.customPaddingTop];
    }

    if (!isHorizontal && showRoundedCorners) {
      return [styles.customPageGridContainerStyles.default];
    }

    if (isThreeByNineLayout && showRoundedCorners) {
      return [styles.customPageGridContainerStyles.threeByNineLayout];
    }

    return [];
  };

  const handleDropdownChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const filterName = event.target.value as PathwayLevel;
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: `collection_filter_chip_${filterName.toLowerCase()}`,
        ...(additionalTrackingData?.pageConfigComponent && {
          pageConfigComponent: additionalTrackingData.pageConfigComponent,
        }),
      },
    });

    onDropdownMenuChange?.(filterName);
  };

  const handleFilterChange = (index: number) => () => {
    const selectedFilter = filters?.[index]?.id.toLowerCase();
    track('click_button', {
      button: { name: 'collections_filter_chip', linkName: `collection_filter_chip_${selectedFilter}` },
      ...(additionalTrackingData?.pageConfigComponent && {
        pageConfigComponent: additionalTrackingData.pageConfigComponent,
      }),
    });

    onFilterChange?.(index);
  };

  const renderImage = () => (
    <div css={styles.collectionsWrapper}>
      <div css={styles.controls}>
        {dropdownMenu && dropdownMenu.options && (
          <React.Fragment>
            <RoundedSelectField
              value={currentDropdownValue}
              onChange={handleDropdownChange}
              aria-label={dropdownMenu.label || ''}
              css={styles.dropdownMenu}
            >
              {dropdownMenu.options.map((option) => (
                <SelectOption key={option.value} value={option.value} css={styles.selectOption}>
                  {option.label}
                </SelectOption>
              ))}
            </RoundedSelectField>
            {hasFilters && <div css={styles.verticalLine} />}
          </React.Fragment>
        )}
        {hasFilters && (
          <div aria-label={_t('Filter By')} css={styles.filters} role="tablist">
            {filters.map(({ filterName, id }, idx) => {
              return (
                <Chip
                  key={id}
                  selected={currentFilterIndex === idx}
                  aria-selected={currentFilterIndex === idx}
                  onClick={handleFilterChange(idx)}
                  aria-label={filterName}
                  role="tab"
                  data-testid={`filter-${idx}`}
                >
                  {filterName}
                </Chip>
              );
            })}
          </div>
        )}
      </div>
      <div css={styles.collection} data-testid="product-cards-collection">
        {isCollectionLoading && (
          <React.Fragment>
            <Hidden smUp css={styles.placeholders}>
              <ProductCardsPlaceholder
                data-testid="placeholder-mobile"
                itemsGridConfig={itemsGridConfig}
                hideTitle={true}
                hideButton={true}
                defaultMobileRows={rowsToDisplay}
                shouldShowListView={true}
              />
            </Hidden>
            <Hidden only="xs" css={styles.placeholders}>
              <ProductCardsPlaceholder
                data-testid="placeholder"
                itemsGridConfig={itemsGridConfig}
                hideTitle={true}
                hideButton={true}
                defaultRows={rowsToDisplay}
                shouldShowListView={false}
              />
            </Hidden>
          </React.Fragment>
        )}
        {!isCollectionLoading && filteredRoles && filteredRoles.length > 0 && (
          <ShowMoreGridSection<CareerRoleType | UpsellCard>
            items={filteredRolesPlusUpsells}
            renderItem={({ item, itemIndex }) => {
              if ('renderComponent' in item) {
                return <div css={styles.upsellContainer}>{item.renderComponent({ index: itemIndex })}</div>;
              }

              const currentFilterId = filters?.[currentFilterIndex]?.id;
              const eventingData = {
                ...((currentDropdownValue || currentFilterId) && {
                  filter: [
                    ...(currentDropdownValue ? [currentDropdownValue] : []),
                    ...(currentFilterId ? [currentFilterId] : []),
                  ],
                }),
                productCard: {
                  recommender: {
                    hash: collection?.recommenderConfigurationHash,
                    context: collection?.context,
                  },
                  index: itemIndex,
                  ...(additionalTrackingData?.pageConfigComponent && {
                    pageConfigComponent: additionalTrackingData.pageConfigComponent,
                  }),
                },
                carousel: {
                  id: collection?.id,
                },
              };

              return (
                <ProductCardComponent
                  eventingData={eventingData}
                  {...item}
                  proficiencyLevel={currentDropdownValue}
                  showPathwaysLoadingState={showPathwaysLoadingState}
                />
              );
            }}
            itemsGridConfig={itemsGridConfig}
            spacing={{ xs: 8, sm: 16 }}
            defaultRows={rowsToDisplay}
            css={styles.showMoreGridSection}
            onExpandButtonClick={onExpandButtonClick}
          />
        )}
        {!isCollectionLoading && !filteredRoles.length && (
          <div css={styles.noResultsFound}>
            <Typography2 variant="subtitleLarge" component="h3">
              {_t('No results found for "#{proficiencyLevel} #{filterName}"', {
                proficiencyLevel: dropdownMenu?.options?.find((option) => option.value === currentDropdownValue)?.label,
                filterName: filters?.[currentFilterIndex]?.filterName,
              })}
            </Typography2>
            <Typography2 variant="bodyPrimary" component="p">
              {_t('Change your filters.')}
            </Typography2>
          </div>
        )}
      </div>
      <Hidden {...(isTwoByTenLayout ? { lgUp: true } : { mdUp: true })}>{button(true)}</Hidden>
    </div>
  );

  const renderContent = () =>
    shouldRenderContentPanel ? (
      <div
        css={
          isHorizontal
            ? [styles.content, overrideContentOffsets ?? styles.contentOffsets(isTwoByTenLayout)]
            : styles.contentWithInlineButton
        }
      >
        <div>
          <Typography2 variant={isHorizontal ? 'titleXSmall' : 'subtitleLarge'} component="h2" color="inherit">
            {headline}
          </Typography2>
          {description && (
            <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
              {description}
            </Typography2>
          )}
        </div>
        <Hidden {...(isTwoByTenLayout ? { mdDown: true } : { smDown: true })}>{button()}</Hidden>
      </div>
    ) : null;

  return (
    <ModuleLayout
      renderContent={renderContent}
      renderImage={renderImage}
      assembly={{
        ...layoutConfig,
        customContentPanelStyles: getCustomPanelStyles('content'),
        customImagePanelStyles: getCustomPanelStyles('image'),
        customPageContainerStyles: getCustomPageGridContainerStyles(),
      }}
    />
  );
}

export default CareerCollectionsModule;
